import { useState, useEffect, useCallback, useRef, useLayoutEffect } from "react"
import { useWidget } from "../../../../providers/widget";
import { GalleryWidgetConfig } from "../../config";
import { useGallery } from "../../providers";
import { useModalPresence, useFocusTrap } from "../../hooks";
import { getProxiedImageUrl } from "../../../../common/image-proxy";
import { useGalleryTour } from "../../use-tour";
import {
  ZOOM_BAND,
  ZOOM_FIT,
  ZOOM_MAX,
  ZOOM_STEP,
  zoomAnchor,
  zoomBounds,
  zoomClamp,
  zoomDamp,
  zoomDampScale,
  zoomTransform,
  type Point,
  type ZoomState,
} from "../../zoom";

/** Ease a released pinch or pan, the button and the keys settle with. */
const SETTLE_TRANSITION = 'transform 0.25s cubic-bezier(0.22, 1, 0.36, 1)'

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches

// The lightbox is mount-gated but its hooks still run during SSR; the
// isomorphic form keeps React from warning that useLayoutEffect does nothing
// on the server (same idiom as widget-core's widget-context).
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect

export default function LightBox() {
  const { options } = useWidget<GalleryWidgetConfig>()
  const { lightBoxImageIndex, setLightBoxImageIndex } = useGallery()
  const { mounted, visible } = useModalPresence(lightBoxImageIndex !== undefined)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [isImageLoading, setIsImageLoading] = useState(false)
  const imageRef = useRef<HTMLImageElement>(null)
  const counterRef = useRef<HTMLDivElement>(null)
  const captionRef = useRef<HTMLDivElement>(null)
  const preloadedImages = useRef<Set<string>>(new Set())
  const modalRef = useRef<HTMLDivElement>(null)
  useFocusTrap(modalRef, lightBoxImageIndex !== undefined)
  
  // #1694: the lightbox walks the browser's order — the tour order when the
  // photos are grouped by room, the gallery order otherwise — as ONE index
  // space, so prev / next cross from a room into the next and the counter
  // counts every photo.
  const { ordered: allImages } = useGalleryTour()

  // ---------------------------------------------------------------------------
  // Zoom (#1694, toolkit#823). A two-finger pinch scales the photo about the
  // fingers and pans with them in the same motion, and the photo holds that
  // zoom after the fingers lift until a pinch brings it back under fit, the
  // header button or the +/- keys take it out, or the photo changes. While
  // zoomed, one finger or the mouse pans. Trackpad pinch and ctrl-wheel zoom
  // about the cursor. The state lives in refs and lands on the <img> as an
  // inline transform: a pinch is a 60fps stream and must not re-render.
  // ---------------------------------------------------------------------------
  const zoomEnabled = options.lightbox.zoom
  const surfaceRef = useRef<HTMLDivElement>(null)
  const zoomRef = useRef<ZoomState>({ ...ZOOM_FIT })
  /** Pointers down on the surface, by id. */
  const pointersRef = useRef<Map<number, Point>>(new Map())
  /** Live pinch: its two pointer ids, the start distance and midpoint, the photo center and the zoom at the start. */
  const pinchRef = useRef<{ a: number; b: number; d0: number; m0: Point; c: Point; z: ZoomState } | null>(null)
  /** Live one-finger pan while zoomed: pointer id, start point, start zoom. */
  const panRef = useRef<{ id: number; x0: number; y0: number; z: ZoomState } | null>(null)
  const settleTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [zoomed, setZoomed] = useState(false)

  // The photo's untransformed center in viewport coordinates: its layout box
  // inside the flex-centered surface, which transforms never move.
  const imgCenter = useCallback((): Point => {
    const surface = surfaceRef.current
    const img = imageRef.current
    if (!surface || !img) return { x: 0, y: 0 }
    const r = surface.getBoundingClientRect()
    return { x: r.left + img.offsetLeft + img.offsetWidth / 2, y: r.top + img.offsetTop + img.offsetHeight / 2 }
  }, [])

  const zoomLimits = useCallback(() => {
    const surface = surfaceRef.current
    const img = imageRef.current
    return { w: img?.offsetWidth ?? 0, h: img?.offsetHeight ?? 0, cw: surface?.clientWidth ?? 0, ch: surface?.clientHeight ?? 0 }
  }, [])

  const applyZoom = useCallback((transition: string) => {
    const img = imageRef.current
    if (!img) return
    img.style.transition = transition
    img.style.transform = zoomTransform(zoomRef.current)
    setZoomed(zoomRef.current.s > 1)
  }, [])

  const clearSettleTimer = () => {
    if (settleTimer.current) {
      clearTimeout(settleTimer.current)
      settleTimer.current = null
    }
  }

  // Ease to a rest state (a pinch or pan released, the button, the keys):
  // under fit snaps back to fit, past the limits settles on them, a pan
  // settles inside the photo's edges.
  const settle = useCallback((target?: ZoomState) => {
    clearSettleTimer()
    zoomRef.current = zoomClamp(target ?? zoomRef.current, zoomLimits(), ZOOM_MAX)
    if (prefersReducedMotion()) {
      applyZoom('none')
      return
    }
    applyZoom(SETTLE_TRANSITION)
    settleTimer.current = setTimeout(() => {
      settleTimer.current = null
      if (imageRef.current) imageRef.current.style.transition = ''
    }, 260)
  }, [applyZoom, zoomLimits])

  // Back to fit with no animation: the photo changed, the view closed.
  const zoomReset = useCallback(() => {
    clearSettleTimer()
    zoomRef.current = { ...ZOOM_FIT }
    pinchRef.current = null
    panRef.current = null
    pointersRef.current.clear()
    const img = imageRef.current
    if (img) {
      if (img.style.transform) {
        // A zoom held the photo. Truly instant: the stylesheet transitions
        // `transform` (gallery.css), so clearing the inline transform alone
        // would shrink the NEXT photo from the old zoom. Pin the transition
        // to the opacity channel only (so the prev/next crossfade keeps
        // running; `transition: none` would cancel it and the next photo
        // would cut in hard), snap the transform, flush, hand back. A settle
        // transition still inline is cleared on the way.
        img.style.transition = ''
        img.style.transitionProperty = 'opacity'
        img.style.transform = ''
        void img.offsetWidth
        img.style.transitionProperty = ''
      } else if (img.style.transition) {
        // Not zoomed, but a settle eased back to fit under 260ms ago: drop
        // its inline transition so the stylesheet's own applies again.
        img.style.transition = ''
      }
    }
    setZoomed(false)
  }, [])

  // The button: fit <-> the step zoom, about the center.
  const zoomToggle = useCallback(() => {
    if (zoomRef.current.s > 1) {
      settle({ ...ZOOM_FIT })
      return
    }
    const c = imgCenter()
    settle(zoomAnchor(zoomRef.current, ZOOM_STEP, c, c, c))
  }, [settle, imgCenter])

  // The keys: one step in or out from wherever the zoom is (settle clamps,
  // so - at fit stays at fit).
  const zoomStep = useCallback((dir: 1 | -1) => {
    const c = imgCenter()
    settle(zoomAnchor(zoomRef.current, zoomRef.current.s * (dir > 0 ? 1.5 : 1 / 1.5), c, c, c))
  }, [settle, imgCenter])

  const pinchStart = (a: number, b: number) => {
    const pa = pointersRef.current.get(a)
    const pb = pointersRef.current.get(b)
    if (!pa || !pb) return
    pinchRef.current = {
      a, b,
      d0: Math.hypot(pb.x - pa.x, pb.y - pa.y) || 1,
      m0: { x: (pa.x + pb.x) / 2, y: (pa.y + pb.y) / 2 },
      c: imgCenter(),
      z: { ...zoomRef.current },
    }
    panRef.current = null
    if (imageRef.current) imageRef.current.style.transition = 'none'
  }

  const pinchMove = () => {
    const pinch = pinchRef.current
    if (!pinch) return
    const a = pointersRef.current.get(pinch.a)
    const b = pointersRef.current.get(pinch.b)
    if (!a || !b) return
    const d = Math.hypot(b.x - a.x, b.y - a.y) || 1
    const m1 = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
    const s = zoomDampScale(pinch.z.s * d / pinch.d0, ZOOM_MAX, ZOOM_BAND)
    const z = zoomAnchor(pinch.z, s, pinch.c, pinch.m0, m1)
    const bounds = zoomBounds(Math.min(ZOOM_MAX, Math.max(1, s)), zoomLimits())
    zoomRef.current = { s, x: zoomDamp(z.x, bounds.x, ZOOM_BAND), y: zoomDamp(z.y, bounds.y, ZOOM_BAND) }
    applyZoom('none')
  }

  const panStart = (id: number) => {
    const p = pointersRef.current.get(id)
    if (!p) return
    panRef.current = { id, x0: p.x, y0: p.y, z: { ...zoomRef.current } }
    if (imageRef.current) imageRef.current.style.transition = 'none'
  }

  const panMove = () => {
    const pan = panRef.current
    if (!pan) return
    const p = pointersRef.current.get(pan.id)
    if (!p) return
    const bounds = zoomBounds(pan.z.s, zoomLimits())
    zoomRef.current = {
      s: pan.z.s,
      x: zoomDamp(pan.z.x + p.x - pan.x0, bounds.x, ZOOM_BAND),
      y: zoomDamp(pan.z.y + p.y - pan.y0, bounds.y, ZOOM_BAND),
    }
    applyZoom('none')
  }

  // Pointer capture keeps up/cancel coming to the surface when a finger
  // leaves it mid-gesture; uncaptured still works, so failures are ignored.
  const capture = (el: Element, id: number) => {
    try { (el as HTMLElement).setPointerCapture?.(id) } catch { /* pointer already gone */ }
  }
  const release = (el: Element, id: number) => {
    try { (el as HTMLElement).releasePointerCapture?.(id) } catch { /* pointer already gone */ }
  }

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!zoomEnabled) return
    if (e.pointerType === 'mouse' && e.button !== 0) return
    // The arrows and the caption sit on the surface; a press on a control is a click.
    if ((e.target as Element | null)?.closest?.('button')) return
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    capture(e.currentTarget, e.pointerId)
    const ids = [...pointersRef.current.keys()]
    if (ids.length >= 2) {
      // Two fingers: a pinch, whatever was under way; a pan hands its finger over.
      pinchStart(ids[ids.length - 2], ids[ids.length - 1])
      return
    }
    if (zoomRef.current.s > 1) panStart(e.pointerId)
  }

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!zoomEnabled) return
    if (pointersRef.current.has(e.pointerId)) {
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    }
    if (pinchRef.current) {
      pinchMove()
      return
    }
    if (panRef.current && panRef.current.id === e.pointerId) panMove()
  }

  // A pointer lifted or lost: close out the pinch or the pan it belonged to.
  const onPointerEnd = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!zoomEnabled) return
    const id = e.pointerId
    pointersRef.current.delete(id)
    release(e.currentTarget, id)
    const pinch = pinchRef.current
    if (pinch) {
      if (id !== pinch.a && id !== pinch.b) return
      pinchRef.current = null
      const rest = [...pointersRef.current.keys()]
      if (rest.length && zoomClamp(zoomRef.current, zoomLimits(), ZOOM_MAX).s > 1) {
        // One finger stays down: it pans from here, off a state clamped on
        // the spot (no ease to cut short).
        zoomRef.current = zoomClamp(zoomRef.current, zoomLimits(), ZOOM_MAX)
        applyZoom('none')
        panStart(rest[0])
        return
      }
      settle()
      return
    }
    if (panRef.current && panRef.current.id === id) {
      panRef.current = null
      settle()
    }
  }

  // A pointer whose up/cancel never reached us (capture lost to the browser,
  // a tab switch mid-gesture) must not linger and turn the next single touch
  // into a pinch. lostpointercapture also follows a normal release, where
  // the id is already gone and this is a no-op.
  const onLostPointerCapture = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!pointersRef.current.has(e.pointerId)) return
    onPointerEnd(e)
  }

  // Desktop: trackpad pinch (a wheel event with ctrlKey) and ctrl-wheel zoom
  // about the cursor. A plain wheel is left alone. A native listener, because
  // React registers wheel as passive and the browser's own page zoom has to
  // be prevented here.
  useEffect(() => {
    const surface = surfaceRef.current
    if (!surface || !zoomEnabled || !mounted) return
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey) return
      e.preventDefault()
      const k = Math.exp(-e.deltaY * (e.deltaMode === 1 ? 0.05 : 0.01))
      const s = Math.min(ZOOM_MAX, Math.max(1, zoomRef.current.s * k))
      const c = imgCenter()
      const m = { x: Number.isFinite(e.clientX) ? e.clientX : c.x, y: Number.isFinite(e.clientY) ? e.clientY : c.y }
      zoomRef.current = zoomClamp(zoomAnchor(zoomRef.current, s, c, m, m), zoomLimits(), ZOOM_MAX)
      applyZoom('none')
    }
    surface.addEventListener('wheel', onWheel, { passive: false })
    return () => surface.removeEventListener('wheel', onWheel)
  }, [zoomEnabled, mounted, applyZoom, imgCenter, zoomLimits])

  // A zoom never carries over to the next photo, nor past a close. A layout
  // effect, so the new photo never paints one frame at the old zoom.
  useIsomorphicLayoutEffect(() => { zoomReset() }, [currentIndex, zoomReset])
  useEffect(() => {
    if (lightBoxImageIndex === undefined) zoomReset()
  }, [lightBoxImageIndex, zoomReset])

  // Preload adjacent images for smoother transitions
  const preloadImage = useCallback((url: string) => {
    if (preloadedImages.current.has(url)) return Promise.resolve()
    
    return new Promise<void>((resolve, reject) => {
      const img = new Image()
      img.onload = () => {
        preloadedImages.current.add(url)
        resolve()
      }
      img.onerror = reject
      img.src = url
    })
  }, [])

  // Preload next and previous images
  useEffect(() => {
    if (allImages.length === 0) return
    
    const preloadAdjacentImages = async () => {
      const nextIndex = currentIndex < allImages.length - 1 ? currentIndex + 1 : 0
      const prevIndex = currentIndex > 0 ? currentIndex - 1 : allImages.length - 1
      
      try {
        await Promise.all([
          preloadImage(getProxiedImageUrl(allImages[nextIndex]?.url, 1600, 1200)),
          preloadImage(getProxiedImageUrl(allImages[prevIndex]?.url, 1600, 1200))
        ])
      } catch (error) {
        console.warn('Failed to preload adjacent images:', error)
      }
    }
    
    preloadAdjacentImages()
  }, [currentIndex, allImages, preloadImage])

  // Update current index when initialImageIndex changes
  useEffect(() => {
    if (lightBoxImageIndex !== undefined) {
      setCurrentIndex(lightBoxImageIndex)
    }
  }, [lightBoxImageIndex])

  const handleClose = useCallback(() => {
    setLightBoxImageIndex(undefined)
  }, [setLightBoxImageIndex])

  const animateImageChange = useCallback(async (newIndex: number) => {
    if (isTransitioning) return
    
    setIsTransitioning(true)
    
    // Check if the image is already preloaded
    const newImage = allImages[newIndex]
    const proxiedUrl = newImage?.url ? getProxiedImageUrl(newImage.url, 1600, 1200) : undefined
    const isImagePreloaded = proxiedUrl && preloadedImages.current.has(proxiedUrl)
    
    // Only show loading if image is not preloaded
    if (!isImagePreloaded) {
      setIsImageLoading(true)
    }
    
    // Add updating class to counter and caption
    if (counterRef.current) {
      counterRef.current.classList.add('updating')
    }
    if (captionRef.current) {
      captionRef.current.classList.add('updating')
    }
    
    // Add fade-out animation class to current image
    if (imageRef.current) {
      imageRef.current.classList.add('fade-out')
    }
    
    // Preload the new image if not already loaded
    if (proxiedUrl && !isImagePreloaded) {
      try {
        await preloadImage(proxiedUrl)
      } catch (error) {
        console.warn('Failed to preload image:', error)
      }
    }
    
    // After animation starts, change the image
    setTimeout(() => {
      setCurrentIndex(newIndex)
      
      // Reset animation classes and add fade-in
      if (imageRef.current) {
        imageRef.current.classList.remove('fade-out')
        imageRef.current.classList.add('fade-in')
      }
      
      // Remove updating classes
      if (counterRef.current) {
        counterRef.current.classList.remove('updating')
      }
      if (captionRef.current) {
        captionRef.current.classList.remove('updating')
      }
      
      // Complete the transition
      setTimeout(() => {
        if (imageRef.current) {
          imageRef.current.classList.remove('fade-in')
        }
        setIsTransitioning(false)
        setIsImageLoading(false)
      }, 300) // Match CSS transition duration
    }, 150) // Half of the transition duration
  }, [isTransitioning, allImages, preloadImage])

  const goToPrevious = useCallback(() => {
    if (isTransitioning) return
    const newIndex = currentIndex > 0 ? currentIndex - 1 : allImages.length - 1
    animateImageChange(newIndex)
  }, [currentIndex, allImages.length, animateImageChange])

  const goToNext = useCallback(() => {
    if (isTransitioning) return
    const newIndex = currentIndex < allImages.length - 1 ? currentIndex + 1 : 0
    animateImageChange(newIndex)
  }, [currentIndex, allImages.length, animateImageChange])

  // Keyboard navigation
  useEffect(() => {
    if (lightBoxImageIndex === undefined) return

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          console.log('Escape from light box', e.key)
          handleClose()
          break
        case 'ArrowLeft':
          goToPrevious()
          break
        case 'ArrowRight':
          goToNext()
          break
        // The keyboard path to the zoom: + / = in, - out. With a modifier
        // the keys belong to the browser (page zoom), never to the lightbox.
        case '+':
        case '=':
          if (zoomEnabled && !e.ctrlKey && !e.metaKey && !e.altKey) {
            e.preventDefault()
            zoomStep(1)
          }
          break
        case '-':
          if (zoomEnabled && !e.ctrlKey && !e.metaKey && !e.altKey) {
            e.preventDefault()
            zoomStep(-1)
          }
          break
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [lightBoxImageIndex, handleClose, goToPrevious, goToNext, zoomEnabled, zoomStep])

  if (allImages.length === 0 || !mounted) return null

  const currentImage = allImages[currentIndex]

  return (
    <div
      ref={modalRef}
      role="dialog"
      aria-modal="true"
      aria-label="Image viewer"
      className={`homerunner-gallery-lightbox-modal fullscreen-modal ${visible ? 'open' : 'closing'}${zoomEnabled ? ' homerunner-gallery-lightbox--zoomable' : ''}${zoomed ? ' homerunner-gallery-lightbox--zoomed' : ''}`}
      style={{ backgroundColor: 'rgba(0, 0, 0, 1)', color: 'white', zIndex: 9999 }}
    >
      {/* Close button - top left */}
      <button
        onClick={handleClose}
        autoFocus
        className="homerunner-gallery-lightbox-close-button absolute top-4 left-4 z-20 flex items-center gap-2 px-3 py-2 bg-black/50 text-white rounded-lg hover:bg-black/70 transition-colors"
        aria-label="Close gallery"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
        <span className="text-sm font-medium">Close</span>
      </button>

      {/* Image counter - top center */}
      <div 
        ref={counterRef}
        className="homerunner-gallery-lightbox-counter absolute top-4 left-1/2 transform -translate-x-1/2 z-20 px-3 py-2 bg-black/50 text-white rounded-lg"
      >
        <span className="homerunner-gallery-lightbox-counter-text text-sm font-medium">
          {currentIndex + 1} / {allImages.length}
        </span>
      </div>

      {/* Zoom toggle - top right: the mouse and keyboard path to the pinch zoom */}
      {zoomEnabled && (
        <button
          type="button"
          onClick={zoomToggle}
          aria-label={zoomed ? 'Zoom out' : 'Zoom in'}
          className={`homerunner-gallery-lightbox-zoom${zoomed ? ' homerunner-gallery-lightbox-zoom--out' : ''} absolute top-4 right-4 z-20 flex items-center justify-center p-2 bg-black/50 text-white rounded-lg hover:bg-black/70 transition-colors`}
        >
          <svg className="homerunner-gallery-zoom-in" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
            <circle cx="11" cy="11" r="7"></circle>
            <line x1="21" y1="21" x2="16.5" y2="16.5"></line>
            <line x1="8" y1="11" x2="14" y2="11"></line>
            <line x1="11" y1="8" x2="11" y2="14"></line>
          </svg>
          <svg className="homerunner-gallery-zoom-out" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
            <circle cx="11" cy="11" r="7"></circle>
            <line x1="21" y1="21" x2="16.5" y2="16.5"></line>
            <line x1="8" y1="11" x2="14" y2="11"></line>
          </svg>
        </button>
      )}

      {/* Main image container — the zoom gesture surface */}
      <div
        ref={surfaceRef}
        className="homerunner-gallery-lightbox-image-container relative w-full h-full flex items-center justify-center p-4"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
        onLostPointerCapture={onLostPointerCapture}
      >
        <img
          ref={imageRef}
          src={getProxiedImageUrl(currentImage.url, 1600, 1200)}
          alt={currentImage.caption || `Image ${currentIndex + 1}`}
          className="homerunner-gallery-lightbox-image max-w-full max-h-full object-contain rounded-lg shadow-2xl"
          style={{ maxHeight: 'calc(100vh - 8rem)' }}
          onDragStart={zoomEnabled ? (e) => e.preventDefault() : undefined}
        />
        
        {/* Loading indicator */}
        {isImageLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/20 rounded-lg">
            <div className="flex items-center gap-2 px-4 py-2 bg-black/50 text-white rounded-lg">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span className="text-sm font-medium">Loading...</span>
            </div>
          </div>
        )}
      </div>

      {/* Navigation arrows - only show if there are multiple images */}
      {allImages.length > 1 && (
        <div className="homerunner-gallery-lightbox-navigation">
          {/* Left arrow - only show if not at first image */}
          {currentIndex > 0 && (
            <button
              onClick={goToPrevious}
              className="homerunner-gallery-lightbox-nav homerunner-gallery-lightbox-nav-prev absolute left-4 top-1/2 transform -translate-y-1/2 z-20 p-3 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
              aria-label="Previous image"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15,18 9,12 15,6"></polyline>
              </svg>
            </button>
          )}

          {/* Right arrow - only show if not at last image */}
          {currentIndex < allImages.length - 1 && (
            <button
              onClick={goToNext}
              className="homerunner-gallery-lightbox-nav homerunner-gallery-lightbox-nav-next absolute right-4 top-1/2 transform -translate-y-1/2 z-20 p-3 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
              aria-label="Next image"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="9,18 15,12 9,6"></polyline>
              </svg>
            </button>
          )}
        </div>
      )}

      {/* Image caption - bottom center */}
      {currentImage.caption && (
        <div 
          ref={captionRef}
          className="homerunner-gallery-lightbox-caption absolute bottom-4 left-1/2 transform -translate-x-1/2 z-20 px-4 py-2 bg-black/50 text-white rounded-lg max-w-md text-center"
        >
          <span className="homerunner-gallery-lightbox-caption-text text-sm font-medium">{currentImage.caption}</span>
        </div>
      )}
    </div>
  )
}
